import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import FilterRow from './FilterRow';
import FilterPicker from './FilterPicker';
import FilterService from './FilterService';
import { getOperatorsForFieldType } from './operators';
import {
  Filter,
  Save,
  X,
  Plus,
  Search,
  CheckSquare,
  Square,
  ListFilter,
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * FilterBuilder Component
 * Main component for building and managing filters based on SystemMetadataController
 */
function FilterBuilder({
  module,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  showApplyButton = true,
  showClearButton = true,
  initialFilters = [],
  onSaveView, // New prop: callback to save view
  columns = [], // New prop: column configuration
  sortConfig = [], // New prop: sort configuration
  datagridKey = null, // New prop: datagrid key identifier
}) {
  const [metadataFields, setMetadataFields] = useState([]);
  const [lookupContextParams] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [showFilterPicker, setShowFilterPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [savingView, setSavingView] = useState(false);
  const hasInitializedRef = React.useRef(false);

  // Sync filters with initialFilters on mount
  // This handles both immediate and async loading scenarios
  useEffect(() => {
    if (!hasInitializedRef.current && initialFilters && Array.isArray(initialFilters)) {
      // Remove logic field from filters
      const filtersWithoutLogic = initialFilters.map((filter) => {
        const { logic, ...filterWithoutLogic } = filter;
        return filterWithoutLogic;
      });
      setFilters(filtersWithoutLogic);
      hasInitializedRef.current = true;
    }
  }, [initialFilters]);

  const displayMetadataFields = metadataFields;

  // Computed values
  const canAddMore = useMemo(
    () => filters.length < displayMetadataFields.length,
    [filters.length, displayMetadataFields.length]
  );
  
  const allFieldsUsed = useMemo(
    () => filters.length >= displayMetadataFields.length && displayMetadataFields.length > 0,
    [filters.length, displayMetadataFields.length]
  );

  // Load metadata on mount or when module changes
  useEffect(() => {
    if (!module) {
      setError('Module is required');
      setLoading(false);
      return;
    }

    const loadMetadata = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await FilterService.getFilterMetadata(module);
        
        if (result.success) {
          // Hide fields that are explicitly marked is_filterable=false/0/'0'.
          // Leave NULL/undefined alone (some envs store inconsistent values).
          const filterable = result.fields.filter(
            (f) =>
              f.is_filterable !== false &&
              f.is_filterable !== 0 &&
              f.is_filterable !== "0"
          );
          const sortedFields = [...filterable].sort(
            (a, b) => (a.filter_order || 0) - (b.filter_order || 0)
          );
          setMetadataFields(sortedFields);
        } else {
          const fallback = FilterService.columnsToMetadataFields(columns);
          if (fallback.length > 0) {
            setMetadataFields(fallback);
            setError(null);
          } else {
            setError(result.error || 'Failed to load filter metadata');
          }
        }
      } catch (err) {
        const fallback = FilterService.columnsToMetadataFields(columns);
        if (fallback.length > 0) {
          setMetadataFields(fallback);
          setError(null);
        } else {
          setError(err.message || 'Failed to load filter metadata');
        }
      } finally {
        setLoading(false);
      }
    };

    loadMetadata();
  }, [module, columns]);

  // Notify parent when filters change
  useEffect(() => {
    onFiltersChange?.(filters);
  }, [filters, onFiltersChange]);

  // Handlers with useCallback for performance
  const handleAddFilter = useCallback(() => {
    // Show filter picker
    setShowFilterPicker(true);
  }, []);

  // Handle field selection from filter picker
  const handleFieldSelect = useCallback((field) => {
    // Check if field is already in filters
    const isAlreadyAdded = filters.some(f => f.field === field.field_key);
    
    if (isAlreadyAdded) {
      // Remove the field if it's already selected (toggle off)
      setFilters(prev => prev.filter(f => f.field !== field.field_key));
      return;
    }

    // Add the field if it's not selected (toggle on)
    const operators = getOperatorsForFieldType(
      field.field_type,
      field.allowed_operators
    );
    const defaultOperator = field.default_operator || operators[0]?.value || 'equals';

    setFilters(prev => [...prev, {
      field: field.field_key,
      operator: defaultOperator,
      value: '',
    }]);

    // If not in multi-select mode, close the picker after adding
    if (!multiSelectMode) {
      setShowFilterPicker(false);
      setSearchQuery('');
    }
  }, [filters, multiSelectMode]);

  const handleFilterChange = useCallback((index, updatedFilter) => {
    setFilters(prev => {
      const newFilters = [...prev];
      // Remove logic field from updated filter
      const { logic, ...filterWithoutLogic } = updatedFilter;
      newFilters[index] = filterWithoutLogic;
      return newFilters;
    });
  }, []);

  const handleDeleteFilter = useCallback((index) => {
    setFilters(prev => {
      const newFilters = prev.filter((_, i) => i !== index);
      return newFilters;
    });
  }, []);

  const INT_MAX = 2147483647;
  const INT_MIN = -2147483648;

  const handleApplyFilters = useCallback(() => {
    for (const filter of filters) {
      const fieldMeta = metadataFields.find(f => f.field_key === filter.field);
      if (!fieldMeta || fieldMeta.ui_type !== 'number') continue;

      const valuesToCheck = Array.isArray(filter.value)
        ? filter.value.filter(v => v !== null && v !== '' && v !== undefined)
        : (filter.value !== null && filter.value !== '' && filter.value !== undefined
            ? [filter.value]
            : []);

      for (const val of valuesToCheck) {
        const numStr = String(val).trim();

        if (isNaN(Number(numStr))) {
          toast.error(`"${fieldMeta.label}" accepts only numeric values.`);
          return;
        }

        if (fieldMeta.field_type === 'integer') {
          const num = Number(numStr);
          if (num > INT_MAX || num < INT_MIN) {
            toast.error(
              `"${fieldMeta.label}" value exceeds the allowed range (max: ${INT_MAX.toLocaleString()}).`
            );
            return;
          }
        }
      }
    }

    onApplyFilters?.(filters);
  }, [onApplyFilters, filters, metadataFields]);

  const handleClearFilters = useCallback(() => {
    setFilters([]);
    onClearFilters?.();
  }, [onClearFilters]);

  const handleSaveViewClick = useCallback(() => {
    setSaveViewModalOpen(true);
    setViewName('');
  }, []);

  const handleSaveViewCancel = useCallback(() => {
    setSaveViewModalOpen(false);
    setViewName('');
  }, []);

  const handleSaveViewConfirm = useCallback(async () => {
    if (!viewName.trim()) {
      return; // Don't save if name is empty
    }

    if (!onSaveView) {
      console.error('onSaveView callback is not provided');
      return;
    }

    setSavingView(true);
    try {
      // Prepare view data
      const viewData = {
        viewName: viewName.trim(),
        filters: filters, // Current filters
        sortConfig: sortConfig, // Sort configuration
        columns: columns, // Column configuration
        datagridKey: datagridKey || module, // Use datagridKey or fallback to module
      };

      await onSaveView(viewData);
      
      // Close modal and reset
      setSaveViewModalOpen(false);
      setViewName('');
    } catch (error) {
      console.error('Failed to save view:', error);
      // Error handling is done in parent component
    } finally {
      setSavingView(false);
    }
  }, [viewName, filters, sortConfig, columns, datagridKey, module, onSaveView]);

  // Get selected field keys for filter picker (must be before early returns)
  const selectedFieldKeys = useMemo(() => {
    return filters.map(f => f.field);
  }, [filters]);

  // Early returns for loading/error states
  if (loading) {
    return (
      <div className="ems-flt-body">
        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ems-flt-body">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  if (metadataFields.length === 0) {
    return (
      <div className="ems-flt-body">
        <Alert severity="info">No filterable fields available for this module.</Alert>
      </div>
    );
  }

  return (
    <>
      {showFilterPicker ? (
        <div className="ems-flt-body ems-flt-pick">
          <div className="ems-flt-pick-top">
            <div className="ems-flt-pick-title">Select a field to filter by</div>
            <div className="ems-flt-pick-actions">
              <button
                className={"ems-btn ems-sm" + (multiSelectMode ? " ems-primary" : "")}
                onClick={() => setMultiSelectMode((m) => !m)}
              >
                {multiSelectMode ? <CheckSquare size={15} /> : <Square size={15} />}
                Multi-select
              </button>
              <button
                className="ems-btn ems-sm ems-ghost"
                onClick={() => {
                  setShowFilterPicker(false);
                  setSearchQuery('');
                  setMultiSelectMode(false);
                }}
              >
                {filters.length ? "Cancel" : "Close"}
              </button>
              {multiSelectMode && (
                <button
                  className="ems-btn ems-sm ems-primary"
                  disabled={filters.length === 0}
                  onClick={() => {
                    setShowFilterPicker(false);
                    setSearchQuery('');
                    setMultiSelectMode(false);
                  }}
                >
                  Done
                </button>
              )}
            </div>
          </div>

          <div className="ems-flt-search">
            <Search size={17} />
            <input
              autoFocus
              placeholder="Search fields…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="ems-flt-pick-scroll">
            <FilterPicker
              module={module}
              columns={columns}
              onFieldSelect={handleFieldSelect}
              selectedFields={selectedFieldKeys}
              searchQuery={searchQuery}
              multiSelectMode={multiSelectMode}
            />
          </div>
        </div>
      ) : filters.length === 0 ? (
        <div className="ems-flt-body ems-flt-empty">
          <div className="ems-flt-empty-ic">
            <ListFilter size={30} />
          </div>
          <div className="ems-flt-empty-title">No filters yet</div>
          <p className="ems-flt-empty-sub">
            Narrow this list down by adding one or more field conditions. Combine them to build
            precise, reusable views.
          </p>
          <button className="ems-btn ems-primary" disabled={!canAddMore} onClick={handleAddFilter}>
            <Plus size={17} strokeWidth={2.3} />
            Add Filter
          </button>
        </div>
      ) : (
        <div className="ems-flt-body">
          <div className="ems-flt-rows-head">
            <span className="ems-flt-rows-title">
              Showing records where <b>all</b> of the following match
            </span>
          </div>
          <div className="ems-flt-rows">
            {filters.map((filter, index) => (
              <div className="ems-flt-row" key={`${filter.field}-${index}`}>
                {index > 0 && <span className="ems-flt-and">AND</span>}
                <FilterRow
                  filter={filter}
                  metadataFields={displayMetadataFields}
                  lookupContextParams={lookupContextParams}
                  onChange={(updatedFilter) => handleFilterChange(index, updatedFilter)}
                  onDelete={handleDeleteFilter}
                  index={index}
                />
              </div>
            ))}
          </div>
          <button className="ems-flt-add" disabled={!canAddMore} onClick={handleAddFilter}>
            <Plus size={16} strokeWidth={2.3} />
            Add Filter
          </button>
          {allFieldsUsed && (
            <Box sx={{ textAlign: 'center', mt: 1.5 }}>
              <Box component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                All available fields are being used. Delete a filter to add a new one.
              </Box>
            </Box>
          )}
        </div>
      )}

      {!showFilterPicker && (
        <div className="ems-flt-foot">
          {showClearButton && (
            <button className="ems-btn ems-ghost" onClick={handleClearFilters}>
              <X size={16} />
              Clear all
            </button>
          )}
          <div className="ems-flt-spacer" />
          <button className="ems-btn ems-ghost" disabled={!onSaveView} onClick={handleSaveViewClick}>
            <Save size={16} />
            Save view
          </button>
          {showApplyButton && onApplyFilters && (
            <button className="ems-btn ems-primary" onClick={handleApplyFilters}>
              <Filter size={16} />
              Apply{filters.filter((f) => f.value !== '' && f.value != null).length > 0 ? ` (${filters.length})` : ''}
            </button>
          )}
        </div>
      )}

      {/* Save View modal */}
      {saveViewModalOpen && (
        <div className="ems-flt-mini-backdrop" onClick={handleSaveViewCancel}>
          <div
            className="ems-card ems-flt-mini"
            role="dialog"
            aria-label="Save View"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="ems-flt-mini-title">Save View</h3>
            <input
              autoFocus
              className="ems-fm-input"
              placeholder="View name"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && viewName.trim() && !savingView) {
                  handleSaveViewConfirm();
                }
              }}
              disabled={savingView}
            />
            <div className="ems-flt-mini-foot">
              <button className="ems-btn ems-ghost" disabled={savingView} onClick={handleSaveViewCancel}>
                Cancel
              </button>
              <button
                className="ems-btn ems-primary"
                disabled={!viewName.trim() || savingView}
                onClick={handleSaveViewConfirm}
              >
                {savingView ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default FilterBuilder;

